import L from 'leaflet';

import { MapOf } from '@/Util';

export type CustomMarkerContext = {
	glyph: string;
	font: string;
	size: number;
	opacity: number;
	color: string;
	latlng: L.LatLng;
	interactive: boolean;
	/** When this point is dithered, where was the original location. When this is set we will draw a line towards it */
	centroid?: L.LatLng;
	backgroundColor?: string;
};

const defaultOptions: CustomMarkerContext = {
	glyph: '·',
	font: '"Font Awesome 5 Free"',
	size: 4,
	opacity: 1,
	color: '#F00',
	latlng: new L.LatLng(0, 0),
	interactive: true,
};


// Yuck
const originalDraw = (L.Canvas.prototype as any)._draw;

L.Canvas.include({
	// very specific patch for our use-case, might break when drawing functions are called externally.
	// computing redraw bounds is slow, and happens very often
	// so queue up these computations and run them all at once using a more streamlined method
	// This greatly speeds up sequential adding/removing of layers in the same frame
	_requestRedraw(this: L.Canvas&MapOf<any>&{_map?: L.Map, _boundsQueue?: Set<L.Bounds>}, l: L.Layer&{_pxBounds: L.Bounds}) {
		if (l._pxBounds && this._map){
			this._boundsQueue = this._boundsQueue || new Set();
			this._boundsQueue.add(l._pxBounds);

			this._redrawRequest = this._redrawRequest || requestAnimationFrame(() => {
				let minx = Number.MAX_SAFE_INTEGER;
				let miny = Number.MAX_SAFE_INTEGER;
				let maxx = Number.MIN_SAFE_INTEGER;
				let maxy = Number.MIN_SAFE_INTEGER;
				for (const lb of this._boundsQueue!.values()) {
					minx = minx < lb.min!.x ? minx : lb.min!.x;
					miny = miny < lb.min!.y ? miny : lb.min!.y;
					maxx = maxx > lb.max!.x ? maxx : lb.max!.x;
					maxy = maxy > lb.max!.y ? maxy : lb.max!.y;
				}
				const padding = 1;
				minx -= padding;
				miny -= padding;
				maxx += padding;
				maxy += padding;

				if (this._redrawBounds) {
					this._redrawBounds.extend([minx + padding, miny + padding]).extend([maxx + padding, maxy + padding]);
				} else {
					this._redrawBounds = new L.Bounds([minx, miny], [maxx, maxy]);
				}

				this._boundsQueue = new Set();
				this._redraw();
			});
		}
	},

	_shouldUpdateContextSettings(marker: CustomMarkerContext): boolean {
		if (!this._drawing) {
			return false;
		}
		
		if (marker.size !== this._lastSize || marker.font !== this._lastFont) { // font switching is relatively slow, do it as little as possible!
			this._lastFont = marker.font;
			this._lastSize = marker.size;
			return true;
		}
		return false;
	},
	_draw() {
		if (this._ctx) {
			// See map implementation....
			this._lastSize = undefined;
			this._ctx.textAlign = 'center';
			this._ctx.textBaseline = 'middle';
			// shadows are unusably slow in Firefox :(
			// this._ctx.shadowBlur = 0;
			// this._ctx.shadowColor = 'rgba(0,0,0,0.25)';
			// this._ctx.shadowOffsetX = this._ctx.shadowOffsetY = Math.ceil(this._customFontSize * 0.075);

			originalDraw.call(this, ...arguments);
		}
	}
});

export class CustomMarker extends L.CircleMarker {
	public _point: L.Point = null as any;
	public _centroid?: L.Point = null as any;
	public _pxBounds: L.Bounds = null as any;
	public _renderer: L.Renderer = null as any; // set on creation
	public _map: L.Map = null as any;
	public options: CustomMarkerContext = Object.assign({}, defaultOptions);

	constructor(options: CustomMarkerContext) {
		super(options.latlng);
		L.Util.setOptions(this, options);
	}

	// These setters are never used by us, but better override them to be safe
	public getLatLng() { return this.options.latlng; }
	public setLatLng(v: L.LatLng): this {
		const oldLatLng = this.options.latlng;
		this.options.latlng = v;
		this.redraw();
		return this.fire('move', { oldLatLng, latlng: v });
	}
	public getRadius(): number { return this.options.size; }
	public setRadius(v: number) { this.options.size = v; return this.redraw(); }
	public setStyle(style: L.PathOptions) { Object.assign(this.options, style); return this; }

	public _project() {
		this._point = this._map.latLngToLayerPoint(this.options.latlng);
		this._centroid = this.options.centroid && this._map.latLngToLayerPoint(this.options.centroid);
		(this as any)._updateBounds();
	}

	public _updateBounds() {
		const r = this.options.size * 0.5;

		const w = this._clickTolerance();
		const p: [number, number] = [r + w, r + w];
		this._pxBounds = new L.Bounds(this._point.subtract(p), this._point.add(p));
	}

	public _clickTolerance(): number {
		return this.options.size * 0.5 + (this._renderer as any).options.tolerance;
	}

	public _updatePath() {
		if ((this._renderer as any)._drawing) {
			const renderer = this._renderer as any;
			const ctx = renderer._ctx as CanvasRenderingContext2D;
			
			// optimization: cache last drawn font settings in renderer, because switching is slow
			// but now we need to check if the font settings need to be updated in the context
			const shouldUpdateContextFont = !('_shouldUpdateFont' in renderer) || renderer._shouldUpdateFont(this.options);
			
			// separate draw implementation so it can be invoked with an external canvas too.
			// (i.e. export to image)
			this.drawOnCanvas(ctx, shouldUpdateContextFont);
		}
	}

	public drawOnCanvas(ctx: CanvasRenderingContext2D, shouldUpdateContextFont: boolean = true) {
		if (this.options.backgroundColor) {
			// background circle
			ctx.beginPath();
			ctx.arc(this._point.x, this._point.y, this.options.size / 2, 0, 2*Math.PI);
			ctx.fillStyle = this.options.backgroundColor;
			ctx.fill();
			// outline
			ctx.beginPath();
			ctx.lineWidth = 1;
			ctx.strokeStyle = 'rgba(0,0,0,0.15)';
			ctx.arc(this._point.x, this._point.y, this.options.size / 2, 0, 2*Math.PI);
			ctx.stroke();
		}

		if (this._centroid) {
			ctx.strokeStyle = 'rgba(0,0,0,0.35)';
			ctx.lineWidth = 1 * this.options.size / 10;
			ctx.beginPath();
			ctx.moveTo(this._point.x, this._point.y);
			ctx.lineTo(this._centroid.x, this._centroid.y);
			ctx.stroke();
		}
		
		const marker = this.options;
		const point = this._point;
		if (shouldUpdateContextFont) 
			ctx.font = `900 ${marker.size || 10}px "${marker.font}"`;
		
		// text-shadow emulation (don't use actual shadow - it's very slow under FireFox)
		ctx.fillStyle = 'black';
		ctx.globalAlpha = 0.3 * marker.opacity;
		ctx.fillText(marker.glyph, point.x + marker.size * 0.08, point.y + marker.size * 0.08);

		// glyph itself
		ctx.fillStyle = marker.color;
		ctx.globalAlpha = marker.opacity;
		ctx.fillText(marker.glyph, point.x, point.y);
	}

	// _empty() can stay as it is
	// This might break, clickTolerance might require some option we don't set.
	public _containsPoint(p: L.Point): boolean {
		return p.distanceTo(this._point) <=  this._clickTolerance();
	}
}

export function addOnDemandLayer<T>(options: {
	map: L.Map,
	layerControl: L.Control.Layers,
	title: string,
	attribution: string,
	url: string,
	createLayers: (data: T) => L.Layer[]
}) {
	const {map, layerControl, title, attribution, url, createLayers} = options;
	const layerGroup = new L.LayerGroup([], {attribution});

	layerControl.addOverlay(layerGroup, title);
	let loaded = false;
	map.on('overlayadd', (e: L.LayersControlEvent) => {
		if (loaded || e.layer !== layerGroup) {
			return;
		}
		loaded = true;

		fetch(url)
		.then(r => r.json())
		.then(data => {
			createLayers(data).forEach(layer => layerGroup.addLayer(layer));
			layerGroup.addTo(map);
		});
	});
}


// modified heavily from https://github.com/valkenburg/Leaflet.Control.DetailLevel
/**
    L.Control.DetailLevel by Wessel Valkenburg, released under MIT License.
    Copyright 2019 Wessel Valkenburg
    Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
    The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
    THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
*/

type DetailLevelControlOptions = {
    tileSize: number;
    maxZoom: number;
    minZoom: number;
    maxNativeZoom: number|undefined;
    minNativeZoom: number|undefined;
    zoomOffset: number;
};
const ourInfo = Symbol('_detailLevelControlInfo');


// augment L.TileLayer with our properties
declare module 'leaflet' {
    interface TileLayerOptions {
        [ourInfo]?: DetailLevelControlOptions;
    }
}

export class DetailControl {
    private internalOffsetCounter = 0;
    private updateLock = false;

    private listeners: { [key: string]: (e: any) => void } = {};

    constructor(
        private map: L.Map,
        private minOffset: number = -3,
        /* default to a maximum offset of 3, since 2^3 = 8 tiles per canonical tile already. */
        private maxOffset: number = 3,
        defaultOffset: number = 0,
    ) {
        minOffset = Math.max(-10, minOffset); // >= -10
        maxOffset = Math.max(0, maxOffset); // >= 0
        this.internalOffsetCounter = Math.max(minOffset, Math.min(maxOffset, defaultOffset)); // clamp to [minOffset, maxOffset]

        this.listeners = {
            "layeradd": (e: any) => this.bindToLayer(e.layer),
            "loading": () => this.updateLock = true,
            "load": () => this.updateLock = false,
        }
        Object.entries(this.listeners).forEach(([k, v]) => map.on(k, v));
        map.eachLayer(l => this.bindToLayer(l)); 
        this.changeDetail(0); // initialize
    }

    public detach() {
        Object.entries(this.listeners).forEach(([k, v]) => this.map.off(k, v));
    }

    public getCurrentoffset() { return this.internalOffsetCounter; }
    public setDetail(offset: number) { this.changeDetail(offset - this.internalOffsetCounter); }
    public increaseDetail() { this.changeDetail(1); }
    public decreaseDetail() { this.changeDetail(-1); }
    public changeDetail(delta: number) {
        if (this.updateLock || !this.canDoZoom(delta)) return;

        this.internalOffsetCounter += delta;
        let newOffset = this.internalOffsetCounter;
        
        let overallMaxZoom = -1;
        let overallMinZoom = 1e30;
        
        this.map.eachLayer(layer => {
            if (!(this.isTileLayer(layer) && this.isAttached(layer))) return;
            let originalSettings = layer.options[ourInfo]!;
            layer.options.tileSize = Math.round(Math.pow(2, -newOffset) * originalSettings.tileSize);
            layer.options.zoomOffset = originalSettings.zoomOffset + newOffset;

            /* use native max / min zooms here:
                leaflet smartly scales the images when we go beyond
                the native zooms. That is exactly what we need:
                if the offset zoom is maxed out, allow for scaling.
                So, adapt the native zooms according to our offset. */
            layer.options.maxNativeZoom = originalSettings.maxNativeZoom ?? originalSettings.maxZoom - newOffset;
            layer.options.minNativeZoom = originalSettings.minNativeZoom ?? originalSettings.minZoom - newOffset;
            
            overallMaxZoom = Math.max(overallMaxZoom, layer.options.maxZoom!);
            overallMinZoom = Math.min(overallMinZoom, layer.options.minZoom!);
            
            // @ts-ignore
            layer._resetGrid();
        });

        /* need to propagate the limits to the global map */
        this.map.setMaxZoom(overallMaxZoom);
        this.map.setMinZoom(overallMinZoom);                

        /* rebuild the view */
        // @ts-ignore
        this.map._resetView(this.map.getCenter(), this.map.getZoom());
    }

    private canDoZoom(delta: number) {
        return this.internalOffsetCounter + delta <= this.maxOffset && this.internalOffsetCounter + delta >= this.minOffset;
    }
    private isTileLayer(l: any): l is L.TileLayer { 
        return l?.options?.zoomOffset != null; 
    }
	private isAttached(l: L.TileLayer) {
		return l.options[ourInfo] != null;
	}

    private bindToLayer(layer: L.Layer) {
        // @ts-ignore
        if (!this.isTileLayer(layer) || this.isAttached(layer) || layer.options.prevent_detail_override) return;
        layer.options[ourInfo] = {
            tileSize : layer.options.tileSize as number,
            maxZoom : layer.options.maxZoom!,
            minZoom : layer.options.minZoom!,
            maxNativeZoom : layer.options.maxNativeZoom,
            minNativeZoom : layer.options.minNativeZoom,
            zoomOffset : layer.options.zoomOffset!,
        };
    }
}
